import { httpClient } from './httpClient';

interface PaginatedData<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export const authApi = {
  login: (username: string, password: string) =>
    httpClient.post<{ token: string; adminId: string; expiresIn: number }>('/api/auth/login', {
      username,
      password,
    }),

  verify: () =>
    httpClient.get<{ adminId: string; verified: boolean }>('/api/auth/verify'),

  logout: () =>
    httpClient.post<{ message: string }>('/api/auth/logout'),
};

export const dashboardApi = {
  getStats: () =>
    httpClient.get<{
      tasks: {
        total: number;
        completed: number;
        failed: number;
        queued: number;
        today: number;
      };
      instances: {
        activeCount: number;
        totalCount: number;
      };
    }>('/api/dashboard/stats'),

  getTrends: (days: number = 7) =>
    httpClient.get<{ trends: Array<{ date: string; completed: number; failed: number; total: number }> }>('/api/dashboard/trends', { params: { days } }),

  getRecentTasks: (limit: number = 10) =>
    httpClient.get<{ tasks: any[] }>('/api/dashboard/recent-tasks', { params: { limit } }),
};

export const tasksApi = {
  getTasks: (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    userId?: string;
    search?: string;
    startDate?: number;
    endDate?: number;
  }) =>
    httpClient.get<PaginatedData<any>>('/api/tasks', { params }),

  getTaskById: (taskId: string) =>
    httpClient.get<{ task: any }>(`/api/tasks/${taskId}`),

  retryTask: (taskId: string) =>
    httpClient.post<{ message: string; taskId: string }>(`/api/tasks/${taskId}/retry`),

  cancelTask: (taskId: string) =>
    httpClient.post<{ message: string; taskId: string }>(`/api/tasks/${taskId}/cancel`),

  batchCancel: (taskIds: string[]) =>
    httpClient.post<{
      data: {
        summary: { success: number; failed: number; total: number };
        results: Array<{ taskId: string; success: boolean; error?: string; message?: string }>;
      };
    }>('/api/tasks/batch/cancel', { taskIds }),

  batchDelete: (taskIds: string[]) =>
    httpClient.post<{
      data: {
        summary: { success: number; failed: number; total: number };
        results: Array<{ taskId: string; success: boolean; error?: string; message?: string }>;
      };
    }>('/api/tasks/batch/delete', { taskIds }),

  exportCSV: (params?: {
    status?: string;
    userId?: string;
    search?: string;
    startDate?: number;
    endDate?: number;
  }) =>
    httpClient.getBlob('/api/tasks/export/csv', { params }),

  // New endpoints for extended features
  getStalledTasks: (timeoutMs?: number) =>
    httpClient.get<{ tasks: any[]; count: number }>(`/api/tasks/stalled`, { params: timeoutMs !== undefined ? { timeoutMs } : undefined }),

  resetStalledTasks: (taskIds: string[], timeoutMs?: number) =>
    httpClient.post<{ count: number; taskIds: string[] }>(`/api/tasks/reset-stalled`, { taskIds, timeoutMs }),

  claimTask: (taskId: string, instanceId: string) =>
    httpClient.post<{ success: boolean; taskId: string; instanceId: string }>(`/api/tasks/claim`, {
      taskId,
      instanceId,
    }),
};

export const instancesApi = {
  getInstances: () =>
    httpClient.get<{ instances: any[] }>('/api/instances'),

  getInstanceStats: () =>
    httpClient.get<{ activeCount: number; totalCount: number; totalActiveTaskCount: number }>('/api/instances/stats'),
};

export const activityLogsApi = {
  getActivityLogs: (params?: {
    page?: number;
    pageSize?: number;
    action?: string;
    startDate?: number;
    endDate?: number;
  }) =>
    httpClient.get<PaginatedData<any>>('/api/activity-logs', { params }),

  exportCSV: (params?: {
    action?: string;
    startDate?: number;
    endDate?: number;
  }) =>
    httpClient.getBlob('/api/activity-logs/export/csv', { params }),
};

export const cacheApi = {
  getInfo: () =>
    httpClient.get<{
      provider: string;
      configuredProvider: string;
      config: {
        listLimit: number;
        defaultTtl: number;
      };
      capabilities: {
        canListKeys: boolean;
        canWrite: boolean;
      };
      lastError: string | null;
    }>('/api/cache/info'),

  getStats: (provider?: string) =>
    httpClient.get<{
      overview: {
        provider: string;
        configuredProvider: string;
        capabilities: {
          canListKeys: boolean;
          canWrite: boolean;
        };
        lastError: string | null;
      };
      counts: {
        totalKeys: number;
        instanceKeys: number;
        taskKeys: number;
        sessionKeys: number;
      };
      samples: {
        allKeys: string[];
        instanceKeys: string[];
        taskKeys: string[];
        sessionKeys: string[];
      };
      config: {
        listLimit: number;
        defaultTtl: number;
      };
    }>('/api/cache/stats', provider ? { params: { provider } } : {}),

  listKeys: (prefix?: string, limit?: number, provider?: string) =>
    httpClient.get<{
      keys: string[];
      count: number;
    }>('/api/cache/keys', { 
      params: { 
        ...(prefix !== undefined && { prefix }),
        ...(limit !== undefined && { limit }),
        ...(provider !== undefined && { provider }),
      }
    }),

  deleteKeys: (options: { keys?: string[]; pattern?: string }, provider?: string) =>
    httpClient.delete<{ message: string }>('/api/cache/keys', { 
      ...options,
      ...(provider !== undefined && { provider }),
    }),

  getValue: (key: string, type?: string, provider?: string) =>
    httpClient.get<any>('/api/cache/value', { 
      params: { 
        key,
        ...(type !== undefined && { type }),
        ...(provider !== undefined && { provider }),
      }
    }),

  setValue: (key: string, value: any, ttl?: number, provider?: string) =>
    httpClient.post<{ message: string }>('/api/cache/value', {
      key,
      value,
      ...(ttl !== undefined && { ttl }),
      ...(provider !== undefined && { provider }),
    }),
};

// System API (debug/health/redis)
export const systemApi = {
  getHealth: () => httpClient.get<any>('/health'),
  getDebugInfo: () => httpClient.get<any>('/api/dashboard/debug'),
  getRedisStatus: () => httpClient.get<any>('/api/redis-status'),
  getApiInfo: () => httpClient.get<any>('/api'),
};
